class KubotaCollectionsListing extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('.kcl__nav-link')];
    this.panels = [...this.querySelectorAll('.kcl__strip')];
    this._tabAbort = new AbortController();

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this._onTabClick, { signal: this._tabAbort.signal });
    });

    this.addEventListener('keydown', this._onKeyDown, { signal: this._tabAbort.signal });

    const initialIndex = Number(this.dataset.activeIndex || '0');
    this._bindActivePanel(initialIndex);
  }

  disconnectedCallback() {
    this._tabAbort?.abort();
    this._dotAbort?.abort();
    this._panelIO?.disconnect();
  }

  _onTabClick = (e) => {
    const idx = Number(e.currentTarget.dataset.index);
    if (Number.isFinite(idx)) this._activate(idx);
  };

  _onKeyDown = (e) => {
    if (!this.tabs.includes(document.activeElement)) return;
    const current = this.tabs.indexOf(document.activeElement);
    const last = this.tabs.length - 1;
    let next = null;

    switch (e.key) {
      case 'ArrowRight':
        next = current === last ? 0 : current + 1;
        break;
      case 'ArrowLeft':
        next = current === 0 ? last : current - 1;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = last;
        break;
      default:
        return;
    }

    e.preventDefault();
    this.tabs[next].focus();
    const blockIdx = Number(this.tabs[next].dataset.index);
    if (Number.isFinite(blockIdx)) this._activate(blockIdx);
  };

  _bindActivePanel(index) {
    this._panelIO?.disconnect();
    this._panelIO = null;
    this._dotAbort?.abort();
    this._dotAbort = null;

    const panel = this.panels.find((p) => Number(p.dataset.index) === index);
    if (!panel) return;

    const cardsContainer = panel.querySelector('.kcl__cards');
    // Filler slots (`.kcl__card-slot--filler`) absorb leftover grid columns when
    // a collection has fewer products than `cards_per_collection`. They are not
    // navigable slides — exclude from IntersectionObserver + dot syncing.
    const slots = [...panel.querySelectorAll('.kcl__card-slot:not(.kcl__card-slot--filler)')];
    const dots = [...panel.querySelectorAll('.kcl__dot')];
    if (!cardsContainer || slots.length === 0) return;

    this._dotAbort = new AbortController();
    dots.forEach((dot) => {
      dot.addEventListener(
        'click',
        (e) => {
          const slideIndex = Number(e.currentTarget.dataset.slideIndex);
          const target = slots[slideIndex];
          if (!target) return;
          const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
          target.scrollIntoView({
            behavior: reduced ? 'auto' : 'smooth',
            inline: 'start',
            block: 'nearest',
          });
        },
        { signal: this._dotAbort.signal }
      );
    });

    this._panelIO = new IntersectionObserver(
      (entries) => {
        let best = null;
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (!best || entry.intersectionRatio > best.intersectionRatio) {
            best = entry;
          }
        });
        if (!best) return;
        const slideIndex = Number(best.target.dataset.slideIndex);
        dots.forEach((d, i) => {
          d.setAttribute('aria-current', i === slideIndex ? 'true' : 'false');
        });
      },
      {
        root: cardsContainer,
        threshold: [0.5, 0.75, 1.0],
      }
    );
    slots.forEach((s) => this._panelIO.observe(s));
  }

  _activate(index) {
    const tab = this.tabs.find((t) => Number(t.dataset.index) === index);
    const panel = this.panels.find((p) => Number(p.dataset.index) === index);
    if (!tab || !panel) return;

    this.dataset.activeIndex = String(index);

    // Active tab's `data-gradient-color` drives the hero radial gradient's
    // outer color via the `--kcl-active-color` custom property on this root.
    // Blank/missing → remove the property so CSS falls back to `var(--color-background)`.
    const gradientColor = tab.dataset.gradientColor || '';
    if (gradientColor) {
      this.style.setProperty('--kcl-active-color', gradientColor);
    } else {
      this.style.removeProperty('--kcl-active-color');
    }

    this.tabs.forEach((t) => {
      const active = Number(t.dataset.index) === index;
      t.setAttribute('aria-selected', active ? 'true' : 'false');
      t.setAttribute('tabindex', active ? '0' : '-1');
    });

    this.panels.forEach((p) => {
      const isActive = Number(p.dataset.index) === index;
      // Visual cross-fade is driven by the `.kcl__strip--active` class
      // (opacity + filter blur 8→0 transition, 450ms cubic-bezier). Inactive
      // panels stay in the DOM (stacked via grid-area) so the transition has
      // a "from" frame to interpolate against; `inert` + `aria-hidden`
      // remove them from focus order and the a11y tree.
      p.classList.toggle('kcl__strip--active', isActive);
      p.toggleAttribute('inert', !isActive);
      p.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    this._bindActivePanel(index);
  }
}

if (!customElements.get('kubota-collections-listing')) {
  customElements.define('kubota-collections-listing', KubotaCollectionsListing);
}
